import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { auth } from '../firebase.js';
import { api } from './api.js';
import type { AppUser, AppSection } from '@gng-ehs/shared';

const SUPER_ADMIN_EMAIL = 'rohan@gandg.com';

interface AuthContextValue {
  user: User | null;
  appUser: AppUser | null;
  loading: boolean;
  isAdmin: boolean;
  canRead: (section: AppSection) => boolean;
  canWrite: (section: AppSection) => boolean;
  refreshUserProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = useCallback(async (firebaseUser: User | null) => {
    if (!firebaseUser) {
      setAppUser(null);
      return;
    }
    try {
      const res = await api.users.getMe();
      if (res.data) {
        setAppUser(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch user profile & permissions:', err);
      // Fallback: If rohan@gandg.com, grant default admin permissions in state
      if (firebaseUser.email?.toLowerCase() === SUPER_ADMIN_EMAIL) {
        setAppUser({
          id: 'usr_admin_rohan',
          email: SUPER_ADMIN_EMAIL,
          full_name: 'Rohan (Admin)',
          role: 'admin',
          permissions: {
            dashboard: 'write',
            training: 'write',
            form9: 'write',
            form10: 'write',
            form11: 'write',
            renewals: 'write',
            companies: 'write',
            equipment: 'write',
            personnel: 'write',
            admin: 'write',
            trash: 'write',
          },

          status: 'active',
        });
      }
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        await fetchUserProfile(u);
      } else {
        setAppUser(null);
      }
      setLoading(false);
    });
    return unsub;
  }, [fetchUserProfile]);

  const refreshUserProfile = useCallback(async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  }, [user, fetchUserProfile]);

  const signIn = useCallback(async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await fetchUserProfile(cred.user);
  }, [fetchUserProfile]);

  const signOut = useCallback(async () => {
    await fbSignOut(auth);
    setAppUser(null);
  }, []);

  const isAdmin = useMemo(() => {
    if (!user) return false;
    const email = (user.email || '').toLowerCase().trim();
    if (email === SUPER_ADMIN_EMAIL) return true;
    if (appUser?.role === 'admin') return true;
    if (appUser?.permissions?.admin === 'write') return true;
    return false;
  }, [user, appUser]);

  const canRead = useCallback((section: AppSection): boolean => {
    if (isAdmin) return true;
    if (!appUser || appUser.status === 'disabled') return false;
    const level = appUser.permissions?.[section] ?? 'read';
    return level === 'read' || level === 'write';
  }, [isAdmin, appUser]);

  const canWrite = useCallback((section: AppSection): boolean => {
    if (isAdmin) return true;
    if (!appUser || appUser.status === 'disabled') return false;
    const level = appUser.permissions?.[section] ?? 'none';
    return level === 'write';
  }, [isAdmin, appUser]);

  const value = useMemo(
    () => ({
      user,
      appUser,
      loading,
      isAdmin,
      canRead,
      canWrite,
      refreshUserProfile,
      signIn,
      signOut,
    }),
    [user, appUser, loading, isAdmin, canRead, canWrite, refreshUserProfile, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
